import React, { Suspense } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';

import { useSkippableFragments } from '../../../../episode-player/hooks/use-skippable-fragments';
import {
    MoviePrerollViewContainer,
    MoviePrerollViewContainerProps,
} from '../../movie/movie-preroll-view/container';
import { useEpisodeCurrentStream } from '../../../../episode-player/hooks/use-episode-current-stream';
import { getNotNullableItems } from '../../../../../lib/get-not-nullable-items';

import { useEpisodeFavoriteToggleWithAnalytics } from './hooks/use-episode-favorite-toggle-with-analytics';

interface EpisodePrerollViewContainerProps
    extends Omit<
        MoviePrerollViewContainerProps,
        'skippableFragments' | 'movieId' | 'isPlannedToWatch' | 'onBookmarkClick'
    > {
    contentId: string;
    filmId: string;
}

interface EpisodePrerollViewProps extends EpisodePrerollViewContainerProps {
    promotedMovieId: number;
}

const EpisodePrerollView = ({
    contentId,
    promotedMovieId,
    filmId,
    ...props
}: EpisodePrerollViewProps) => {
    const skippableFragments = useSkippableFragments(contentId);

    const { onEpisodeFavoriteToggleWithAnalylics, isPlannedToWatch, promotedContentId } =
        useEpisodeFavoriteToggleWithAnalytics({
            seriesId: filmId,
            promotedMovieId,
        });

    if (!promotedContentId) {
        return null;
    }

    return (
        <MoviePrerollViewContainer
            skippableFragments={skippableFragments}
            movieId={promotedMovieId}
            isPlannedToWatch={Boolean(isPlannedToWatch)}
            onBookmarkClick={onEpisodeFavoriteToggleWithAnalylics}
            {...props}
        />
    );
};

export const EpisodePrerollViewContainer = ({
    contentId,
    ...props
}: EpisodePrerollViewContainerProps) => {
    const stream = useEpisodeCurrentStream(contentId);

    const prerolls = getNotNullableItems(stream?.streamMeta?.prerolls);
    const promotedMovieId = prerolls[0]?.promotedEntityId || 0;

    if (!promotedMovieId) {
        return null;
    }

    return (
        <ErrorBoundary fallback={() => null}>
            <Suspense fallback={null}>
                <EpisodePrerollView
                    contentId={contentId}
                    promotedMovieId={promotedMovieId}
                    {...props}
                />
            </Suspense>
        </ErrorBoundary>
    );
};
